

import { createApiCall, paginatedApiCall } from "services/api/createApiCall";
import { DropdownCacheKey, invalidateDropdownCache } from "services/hooks/useDropdownCache";
import { filesApi } from "services/api/config";
import { getAccessToken } from "services/auth";
import { FleetVehicleOfferRent, FleetVehicleOfferRentEditFormValues, FleetVehicleOfferRentRow, FleetVehicleOfferSale, FleetVehicleOfferSaleEditFormValues, FleetVehicleOfferSaleRow, OfferRentSearchOptions, OfferRentStatus, OfferSaleSearchOptions, OfferSaleStatus } from "./types";
import { EmailSendData } from "ui/components/Modal/EmailSendModal/types";
import { FileExtended } from "ui/components/FileUpload/FileDropZone/types";
import { transformFleetVehicleOfferRent, transformFleetVehicleOfferRentOnCreate, transformFleetVehicleOfferRentOnUpdate, transformFleetVehicleOfferRentRow, transformFleetVehicleOfferSale, transformFleetVehicleOfferSaleOnCreate, transformFleetVehicleOfferSaleOnUpdate, transformFleetVehicleOfferSaleRow, transformOfferRentStatus, transformOfferSaleStatus, transformPdfPreviewToFileExtended } from "./transformations";
import { transformErrorResponse } from "services/api/transformation";
import { Pagination } from "services/search";
import { AvailabilityResult, DataWithPagination, transformAvailabilityResult } from "services/api/types";
import { showNotification } from "services/api/snackbar/index";

/*---------------------------------------------------------------------------------------
   GET - OFFER STATUSES
-----------------------------------------------------------------------------------------*/
export const getOfferSaleStatusesAPI = async (): Promise<OfferSaleStatus[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/sale/statuses`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data?.map(transformOfferSaleStatus) ?? [];
};

/*---------------------------------------------------------------------------------------
   SEARCH - VEHICLE - OFFER - SALE - GENERATE OFFER NUMBER
-----------------------------------------------------------------------------------------*/
export const getOfferSaleGenerateNumberAPI = async (
): Promise<string> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/sale/generatenumber`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();




  return response.data?.offerNumber;
};

/*---------------------------------------------------------------------------------------
   GET - OFFER SALE - IS AVAILABLE
-----------------------------------------------------------------------------------------*/
export const getOfferSaleIsAvailableAPI = async (
  vehicleId?: string | null
): Promise<AvailabilityResult> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/sale/isavailable${vehicleId ? `?vehicleId=${vehicleId}` : ""}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformAvailabilityResult(response.data);
};

/*---------------------------------------------------------------------------------------
   SEARCH - VEHICLE - INFO
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleOfferSaleAPI = async (
  id: string
): Promise<FleetVehicleOfferSale> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/sale/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleOfferSale(response.data);
};

/*---------------------------------------------------------------------------------------
  SEARCH - VEHICLE - OFFER
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleOfferSalesAPI = async (
  pagination: Pagination,
  options?: OfferSaleSearchOptions,
): Promise<DataWithPagination<FleetVehicleOfferSaleRow>> => {

  let path = `/v1/fleet/vehicles/offers/sale?`;

  if (options?.vehicleId) path += `vehicleId=${options.vehicleId}`;
  if (options?.offerSaleStatusId) path += `&offerSaleStatusId=${options.offerSaleStatusId}`;
  if (options?.search) path += `&search=${encodeURIComponent(options.search)}`;
  if (options?.showLinkedToContract === false) path += `&showLinkedToContract=false`;
  if (options?.showCancelled === false) path += `&showCancelled=false`;
  if (options?.showItems === false) path += `&showItems=false`;
  if (options?.showVehicle === false) path += `&showVehicle=false`;
  if (options?.showContacts === false) path += `&showContacts=false`;
  if (options?.showStatus === false) path += `&showStatus=false`;
  if (options?.showPdfUpload === false) path += `&showPdfUpload=false`;
  if (options?.offerDateFrom) path += `&offerDateFrom=${new Date(options.offerDateFrom).toISOString()}`;
  if (options?.offerDateTo) path += `&offerDateTo=${new Date(options.offerDateTo).toISOString()}`;
  if (options?.dueDateFrom) path += `&dueDateFrom=${new Date(options.dueDateFrom).toISOString()}`;
  if (options?.dueDateTo) path += `&dueDateTo=${new Date(options.dueDateTo).toISOString()}`;
  if (options?.totalMin) path += `&totalMin=${options.totalMin}`;
  if (options?.totalMax) path += `&totalMax=${options.totalMax}`;
  if (options?.contactClientId) path += `&contactClientId=${options.contactClientId}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleOfferSaleRow
  );
};
/*---------------------------------------------------------------------------------------
  CREATE - VEHICLE - OFFER
-----------------------------------------------------------------------------------------*/

export const createFleetVehicleOfferSaleAPI = async (
  vehicleId: string,
  formValues: FleetVehicleOfferSaleEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleOfferSale> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/offers/sale`,
      method: "POST",
      body: transformFleetVehicleOfferSaleOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.vehicle.offer.sale.create.message.success")}`,
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetOfferSales);
  return transformFleetVehicleOfferSale(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT - VEHICLE - OFFER
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleOfferSaleAPI = async (
  id: string,
  formValues: FleetVehicleOfferSaleEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleOfferSale> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${formValues.vehicleId}/offers/sale/${id}`,
      method: "PUT",
      body: transformFleetVehicleOfferSaleOnUpdate(id, formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.vehicle.offer.sale.edit.message.success")}`,
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetOfferSales);
  return transformFleetVehicleOfferSale(response.data);
};



/*---------------------------------------------------------------------------------------
  DELETE - VEHICLE - OFFER
-----------------------------------------------------------------------------------------*/

export const deleteFleetVehicleOfferSaleAPI = async (
  vehicleId: string,
  offerId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/offers/sale/${offerId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.offer.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetOfferSales);
  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
   PDF
-----------------------------------------------------------------------------------------*/
// export const getFleetVehicleOfferSalePdfAPI = async (
//   id: string
// ): Promise<FileExtended> => {
//   const response = await createApiCall(
//     {
//       path: `/v1/fleet/vehicles/offers/sale/${id}/pdf`,
//       method: "GET",
//       responseType: "blob",
//     },
//     {
//       getMessage: {
//         error: (error: any) => `${error.response.data.message}`,
//       },
//     }
//   )();

//   return transformPdfPreviewToFileExtended(response.data, "Ponuda.pdf");
// };

/*---------------------------------------------------------------------------------------
   PREVIEW - PDF 
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleOfferSalePdfPreviewAPI = async (
  offerId: string,
  fileName: string
): Promise<FileExtended> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/sale/${offerId}/pdf/preview`,
      method: "GET",
      responseType: "blob",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  console.log('test', response)

  return transformPdfPreviewToFileExtended(response.data, fileName);
};

/*---------------------------------------------------------------------------------------
   SEND - OFFER SALE - EMAIL
-----------------------------------------------------------------------------------------*/
export const sendFleetVehicleOfferSaleEmailAPI = async (
  offerId: string,
  emailData: EmailSendData
): Promise<boolean> => {
  const formData = new FormData();

  emailData.recipients.forEach((r) => formData.append("Recipients", r));
  formData.append("ReplyTo", emailData.replyTo);
  formData.append("Subject", emailData.subject);
  formData.append("Content", emailData.content);

  if (emailData.cc) {
    formData.append("CC", emailData.cc);
  }

  for (let index = 0; index < emailData.attachments.length; index++) {
    const file = emailData.attachments[index];
    formData.append(`Attachments[${index}].Id`, file.id);
    formData.append(`Attachments[${index}].Title`, file.title ?? file.name);
    formData.append(`Attachments[${index}].IsNew`, String(file.isNew ?? false));

    if (file.url) {
      formData.append(`Attachments[${index}].Url`, file.url);
      const response = await fetch(file.url);
      const blob = await response.blob();
      formData.append(`Attachments[${index}].File`, blob, file.name);
    }
  }

  const config = {
    headers: { Authorization: `Bearer ${getAccessToken()}` },
  };

  const response = await filesApi.post(
    `/v1/fleet/vehicles/offers/sale/${offerId}/send-email`,
    formData,
    config
  );

  const success = response.data.sent;

  if (success) {
    showNotification("Email je uspješno poslan", { variant: "success" });
  } else {
    showNotification("Slanje e-maila nije uspjelo", { variant: "error" });
  }

  return success;
};

// export const sendFleetVehicleOfferSaleEmailAPI = async (
//   offerId: string,
//   emailData: EmailSendData
// ): Promise<boolean> => {

//   const response = await createApiCall(
//     {
//       path: `/v1/fleet/vehicles/offers/sale/${offerId}/send-email`,
//       method: "POST",
//       body: emailData,
//     },
//     {
//       getMessage: {
//         success: () => "Email je uspješno poslan",
//         error: (error: any) => `${error.response.data.message}`,
//       },
//     }
//   )();

//   return true;
// };

export const toggleOfferSaleStatusHistoryAPI = async (
  offerId: string,
  statusId: string,
  checked: boolean
): Promise<FleetVehicleOfferSale> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/sale/${offerId}/status-history/${statusId}`,
      method: "PUT",
      body: { checked },
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleOfferSale(response.data);
};

export const getPaymentQrPreviewAPI = async (
  offerId: string,
  amount: number
): Promise<string> => {
  const response = await filesApi.get(
    `/v1/fleet/vehicles/offers/sale/${offerId}/payment-qr?amount=${amount}`,
    {
      responseType: "blob",
      headers: { Authorization: `Bearer ${getAccessToken()}` },
    }
  );

  return URL.createObjectURL(response.data);
};

export const sendPaymentQrAPI = async (
  offerId: string,
  data: {
    amount: number;
    recipients: string[];
    cc?: string;
    replyTo: string;
    subject: string;
    content: string;
  }
): Promise<{ sent: boolean }> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/sale/${offerId}/send-payment-qr`,
      method: "POST",
      body: data,
    },
    {
      getMessage: {
        success: () => "QR kod za plaćanje je uspješno poslan",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};

/*---------------------------------------------------------------------------------------
   GET - OFFER RENT STATUSES
-----------------------------------------------------------------------------------------*/
export const getOfferRentStatusesAPI = async (): Promise<OfferRentStatus[]> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/rent/statuses`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data?.map(transformOfferRentStatus) ?? [];
};

/*---------------------------------------------------------------------------------------
   SEARCH - VEHICLE - OFFER - RENT - GENERATE OFFER NUMBER
-----------------------------------------------------------------------------------------*/
export const getOfferRentGenerateNumberAPI = async (
): Promise<string> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/rent/generatenumber`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data?.offerNumber;
};

/*---------------------------------------------------------------------------------------
   GET - OFFER RENT - IS AVAILABLE
-----------------------------------------------------------------------------------------*/
export const getOfferRentIsAvailableAPI = async (
  vehicleId?: string | null
): Promise<AvailabilityResult> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/rent/isavailable${vehicleId ? `?vehicleId=${vehicleId}` : ""}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformAvailabilityResult(response.data);
};

/*---------------------------------------------------------------------------------------
   GET - VEHICLE - OFFER RENT
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleOfferRentAPI = async (
  id: string
): Promise<FleetVehicleOfferRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/rent/${id}`,
      method: "GET",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleOfferRent(response.data);
};

/*---------------------------------------------------------------------------------------
  SEARCH - VEHICLE - OFFER RENT
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleOfferRentsAPI = async (
  pagination: Pagination,
  options?: OfferRentSearchOptions,
): Promise<DataWithPagination<FleetVehicleOfferRentRow>> => {

  let path = `/v1/fleet/vehicles/offers/rent?`;

  if (options?.vehicleId) path += `vehicleId=${options.vehicleId}`;
  if (options?.offerRentStatusId) path += `&offerRentStatusId=${options.offerRentStatusId}`;
  if (options?.search) path += `&search=${encodeURIComponent(options.search)}`;
  if (options?.showLinkedToContract === false) path += `&showLinkedToContract=false`;
  if (options?.showCancelled === false) path += `&showCancelled=false`;
  if (options?.showItems === false) path += `&showItems=false`;
  if (options?.showVehicle === false) path += `&showVehicle=false`;
  if (options?.showContacts === false) path += `&showContacts=false`;
  if (options?.showStatus === false) path += `&showStatus=false`;
  if (options?.showPdfUpload === false) path += `&showPdfUpload=false`;
  if (options?.offerDateFrom) path += `&offerDateFrom=${new Date(options.offerDateFrom).toISOString()}`;
  if (options?.offerDateTo) path += `&offerDateTo=${new Date(options.offerDateTo).toISOString()}`;
  if (options?.dueDateFrom) path += `&dueDateFrom=${new Date(options.dueDateFrom).toISOString()}`;
  if (options?.dueDateTo) path += `&dueDateTo=${new Date(options.dueDateTo).toISOString()}`;
  if (options?.totalMin) path += `&totalMin=${options.totalMin}`;
  if (options?.totalMax) path += `&totalMax=${options.totalMax}`;
  if (options?.contactClientId) path += `&contactClientId=${options.contactClientId}`;

  return await paginatedApiCall(
    path,
    pagination,
    transformFleetVehicleOfferRentRow
  );
};

/*---------------------------------------------------------------------------------------
  CREATE - VEHICLE - OFFER RENT
-----------------------------------------------------------------------------------------*/
export const createFleetVehicleOfferRentAPI = async (
  vehicleId: string,
  formValues: FleetVehicleOfferRentEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleOfferRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/offers/rent`,
      method: "POST",
      body: transformFleetVehicleOfferRentOnCreate(formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.vehicle.offer.rent.create.message.success")}`,
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetOfferRents);
  return transformFleetVehicleOfferRent(response.data);
};

/*---------------------------------------------------------------------------------------
  EDIT - VEHICLE - OFFER RENT
-----------------------------------------------------------------------------------------*/
export const updateFleetVehicleOfferRentAPI = async (
  id: string,
  formValues: FleetVehicleOfferRentEditFormValues,
  t: (key: string) => string
): Promise<FleetVehicleOfferRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${formValues.vehicleId}/offers/rent/${id}`,
      method: "PUT",
      body: transformFleetVehicleOfferRentOnUpdate(id, formValues),
    },
    {
      getMessage: {
        success: () => `${t("admin.fleet.vehicle.offer.rent.edit.message.success")}`,
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetOfferRents);
  return transformFleetVehicleOfferRent(response.data);
};

/*---------------------------------------------------------------------------------------
  DELETE - VEHICLE - OFFER RENT
-----------------------------------------------------------------------------------------*/
export const deleteFleetVehicleOfferRentAPI = async (
  vehicleId: string,
  offerId: string,
  t: (key: string) => string
): Promise<boolean> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/${vehicleId}/offers/rent/${offerId}`,
      method: "DELETE",
    },
    {
      getMessage: {
        success: () => t("admin.fleet.vehicle.offer.message.delete"),
        error: (error: any) => `${transformErrorResponse(error)}`,
      },
    }
  )();

  invalidateDropdownCache(DropdownCacheKey.FleetOfferRents);
  return response.data?.deleted;
};

/*---------------------------------------------------------------------------------------
   PREVIEW - PDF - OFFER RENT
-----------------------------------------------------------------------------------------*/
export const getFleetVehicleOfferRentPdfPreviewAPI = async (
  offerId: string,
  fileName: string
): Promise<FileExtended> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/rent/${offerId}/pdf/preview`,
      method: "GET",
      responseType: "blob",
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformPdfPreviewToFileExtended(response.data, fileName);
};

/*---------------------------------------------------------------------------------------
   TOGGLE - OFFER RENT STATUS HISTORY
-----------------------------------------------------------------------------------------*/
export const toggleOfferRentStatusHistoryAPI = async (
  offerId: string,
  statusId: string,
  checked: boolean
): Promise<FleetVehicleOfferRent> => {
  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/rent/${offerId}/status-history/${statusId}`,
      method: "PUT",
      body: { checked },
    },
    {
      getMessage: {
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return transformFleetVehicleOfferRent(response.data);
};

/*---------------------------------------------------------------------------------------
   SEND - OFFER RENT - EMAIL
-----------------------------------------------------------------------------------------*/
export const sendFleetVehicleOfferRentEmailAPI = async (
  offerId: string,
  emailData: EmailSendData
): Promise<boolean> => {
  const formData = new FormData();

  emailData.recipients.forEach((r) => formData.append("Recipients", r));
  formData.append("ReplyTo", emailData.replyTo);
  formData.append("Subject", emailData.subject);
  formData.append("Content", emailData.content);

  if (emailData.cc) {
    formData.append("CC", emailData.cc);
  }

  for (let index = 0; index < emailData.attachments.length; index++) {
    const file = emailData.attachments[index];
    formData.append(`Attachments[${index}].Id`, file.id);
    formData.append(`Attachments[${index}].Title`, file.title ?? file.name);
    formData.append(`Attachments[${index}].IsNew`, String(file.isNew ?? false));

    if (file.isNew && file instanceof File) {
      formData.append(`Attachments[${index}].File`, file);
    }
  }

  const response = await createApiCall(
    {
      path: `/v1/fleet/vehicles/offers/rent/${offerId}/send-email`,
      method: "POST",
      body: formData,
      headers: { "Content-Type": "multipart/form-data" },
    },
    {
      getMessage: {
        success: () => "Email je uspješno poslan",
        error: (error: any) => `${error.response.data.message}`,
      },
    }
  )();

  return response.data;
};